import React, { useState, useRef } from 'react';
import { makeStyles, Switch, Dropdown, Option } from '@fluentui/react-components';
import {
  ImageEdit20Regular,
  Copy20Regular,
  Delete20Regular,
  ArrowUp20Regular,
  ArrowDown20Regular,
  MoreHorizontal20Regular,
  ArrowUpload20Regular,
} from '@fluentui/react-icons';
import { FileUploadQuestion as FileUploadQuestionType } from '../../../../types/forms';

const useStyles = makeStyles({
  questionCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    width: '100%',
    position: 'relative',
  },
  topBar: {
    height: '5px',
    backgroundColor: '#03787c',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    paddingBottom: '10px',
  },
  canvas: {
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
    padding: '0 20px',
  },
  actionButtons: {
    display: 'flex',
    gap: '28px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    cursor: 'pointer',
    paddingTop: '16px',
  },
  actionIcon: {
    fontSize: '20px',
    color: '#616161',
    cursor: 'pointer',
    '&:hover': {
      color: '#03787c',
    },
  },
  questionSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: '100%',
  },
  questionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  questionTitleRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    width: '100%',
  },
  questionNumber: {
    width: '20px',
    height: '20px',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    color: '#292929',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  questionInput: {
    flex: 1,
    height: '32px',
    backgroundColor: 'white',
    borderRadius: '4px',
    position: 'relative',
    '& input': {
      border: 'none',
      outline: 'none',
      width: '100%',
      height: '100%',
      padding: '6px 40px 6px 10px',
      fontFamily: '"Segoe UI", sans-serif',
      fontSize: '14px',
      color: '#242424',
      backgroundColor: 'transparent',
    },
  },
  inputFocusBorder: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: '#02787c',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
  },
  imageEditIcon: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    color: '#616161',
    '&:hover': {
      color: '#03787c',
    },
  },
  uploadPreview: {
    paddingLeft: '32px',
    width: 'calc(100% - 32px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  uploadButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: 'white',
    border: '1px solid #e1e1e1',
    borderRadius: '4px',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    color: '#242424',
    cursor: 'default',
    width: 'fit-content',
  },
  uploadIcon: {
    fontSize: '20px',
    color: '#616161',
  },
  settingsRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  settingGroup: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  settingLabel: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    color: '#242424',
  },
  dropdown: {
    minWidth: '100px',
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: '#e1e1e1',
  },
  settingsBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px 0 16px',
    height: '44px',
  },
  settingsGroup: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  toggleGroup: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    padding: '8px',
  },
  toggleLabel: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#242424',
  },
  moreIcon: {
    cursor: 'pointer',
    color: '#616161',
    '&:hover': {
      color: '#03787c',
    },
  },
});

// Copilot icon component
const CopilotIcon: React.FC<{ className?: string }> = ({ className }) => (
  <img 
    src="/src/assets/office/ribbon/Copilot.svg" 
    alt="Copilot" 
    className={className}
    style={{ width: '20px', height: '20px' }}
  />
);

interface FileUploadQuestionProps {
  question: FileUploadQuestionType;
  questionNumber: number;
  onChange: (question: FileUploadQuestionType) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export const FileUploadQuestion: React.FC<FileUploadQuestionProps> = ({
  question,
  questionNumber,
  onChange,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
}) => {
  const styles = useStyles();
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const handleTitleChange = (value: string) => {
    onChange({ ...question, title: value });
  };

  const handleMaxSizeChange = (value: string) => {
    onChange({ ...question, maxSize: parseInt(value) });
  };

  const handleToggleRequired = () => {
    onChange({ ...question, required: !question.required });
  };

  return (
    <div className={styles.questionCard}>
      <div className={styles.topBar} />
      
      <div className={styles.contentWrapper}>
        <div className={styles.canvas}>
          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <CopilotIcon className={styles.actionIcon} />
            <Copy20Regular className={styles.actionIcon} onClick={onDuplicate} />
            <Delete20Regular className={styles.actionIcon} onClick={onDelete} />
            <ArrowUp20Regular className={styles.actionIcon} onClick={onMoveUp} />
            <ArrowDown20Regular className={styles.actionIcon} onClick={onMoveDown} />
          </div>

          <div className={styles.questionSection}>
            <div className={styles.questionHeader}>
              {/* Question Title */}
              <div className={styles.questionTitleRow}>
                <div className={styles.questionNumber}>{questionNumber}.</div>
                <div className={styles.questionInput}>
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={question.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    onFocus={() => setIsTitleFocused(true)}
                    onBlur={() => setIsTitleFocused(false)}
                    placeholder="Question"
                  />
                  {isTitleFocused && <div className={styles.inputFocusBorder} />}
                  <ImageEdit20Regular className={styles.imageEditIcon} />
                </div>
              </div>
            </div>

            {/* Upload Preview */}
            <div className={styles.uploadPreview}>
              <div className={styles.uploadButton}>
                <ArrowUpload20Regular className={styles.uploadIcon} />
                Upload file
              </div>
              
              {/* Settings */}
              <div className={styles.settingsRow}>
                <div className={styles.settingGroup}>
                  <span className={styles.settingLabel}>Single file size limit:</span>
                  <Dropdown
                    className={styles.dropdown}
                    value={question.maxSize?.toString() || '10'}
                    selectedOptions={[question.maxSize?.toString() || '10']}
                    onOptionSelect={(_, data) => handleMaxSizeChange(data.optionValue as string)}
                  >
                    <Option text="1 MB" value="1">1 MB</Option>
                    <Option text="5 MB" value="5">5 MB</Option>
                    <Option text="10 MB" value="10">10 MB</Option>
                    <Option text="50 MB" value="50">50 MB</Option>
                    <Option text="100 MB" value="100">100 MB</Option>
                  </Dropdown>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Settings Bar */}
        <div className={styles.settingsBar}>
          <div />
          <div className={styles.settingsGroup}>
            <div className={styles.toggleGroup}>
              <Switch
                checked={question.required}
                onChange={handleToggleRequired}
              />
              <span className={styles.toggleLabel}>Required</span>
            </div>
            <MoreHorizontal20Regular className={styles.moreIcon} />
          </div>
        </div>
      </div>
    </div>
  );
};
